import {itemizableService} from "../services/itemizableService";
import servicesService from "@/modules/common/services/servicesService"
import {productService} from "@/modules/products/services/productService.js";
import packagesService from "../../common/services/packagesService";
import {matterService} from "@/modules/matters/services/matterService";
import {contactService, postMessageService} from "@/modules/common/services";
import {paymentsService, paymentsStatusesService} from "@/modules/payments/services";
import {settingsService} from "../../settings/services/settingsService";

const state = {
  invoice: null,
  estimate: null,
  blankEstimate: null,
  serviceCategories: [],
  servicesLoaded: false,
  productsLoaded: false,
  packagesLoaded: false,
  serviceList: null,
  openPayments: null,
  matterPayments: null,
  matter: null,
  contact: null,
  settings: null,
  paymentAsDeposit: null,
  requestedDeposit: null,
  balanceError: null,
  grandTotal: 0,
  fetchingDeposit: false,
}

const getters = {
  invoice: (state) => state.invoice,
  estimate: (state) => state.estimate,
  serviceCategories: (state) => state.serviceCategories,
  isCatalogItemsLoaded: (state) => state.servicesLoaded && state.productsLoaded && state.packagesLoaded,
  serviceList: (state) => state.serviceList,
  openPayments: (state) => state.openPayments,
  matterPayments: (state) => state.matterPayments,
  matter: (state) => state.matter,
  contact: (state) => state.contact,
  settings: (state) => state.settings,
  paymentAsDeposit: (state) => state.paymentAsDeposit,
  requestedDeposit: (state) => state.requestedDeposit,
  balanceError: (state) => state.balanceError,
  grandTotal: (state) => state.grandTotal,
  fetchingDeposit: (state) => state.fetchingDeposit,
}

const mutations = {
  setInvoice(state, invoice) {
    state.invoice = invoice;
  },
  setEstimate(state, estimate) {
    state.estimate = estimate;
  },
  setBlankEstimate(state, blankEstimate) {
    state.blankEstimate = blankEstimate;
  },
  setServiceList(state, serviceList) {
    state.serviceList = serviceList
  },
  setOpenPayments(state, openPayments) {
    state.openPayments = openPayments;
  },
  setMatterPayments(state, matterPayments) {
    state.matterPayments = matterPayments;
  },
  setMatter(state, matter) {
    state.matter = matter;
  },
  setContact(state, contact) {
    state.contact = contact;
  },
  addCategory(state, category) {
    let categoryIndex = state.serviceCategories.findIndex(c => c.id === category.id);
    if (categoryIndex === -1) {
      categoryIndex = state.serviceCategories.length;
    }
    state.serviceCategories.splice(categoryIndex, 1, category);

    state.serviceCategories = state.serviceCategories.sort((a, b) => {
      const aPosition = a.position ?? state.serviceCategories.length;
      const bPosition = b.position ?? state.serviceCategories.length;
      return aPosition - bPosition;
    });
  },
  setServicesLoaded(state, servicesLoaded) {
    state.servicesLoaded = servicesLoaded;
  },
  setProductsLoaded(state, productsLoaded) {
    state.productsLoaded = productsLoaded;
  },
  setPackagesLoaded(state, packagesLoaded) {
    state.packagesLoaded = packagesLoaded;
  },
  setSettings(state, settings) {
    state.settings = settings;
  },
  setPaymentAsDeposit(state, paymentAsDeposit) {
    state.paymentAsDeposit = paymentAsDeposit;
  },
  setRequestedDeposit(state, requestedDeposit) {
    state.requestedDeposit = requestedDeposit;
  },
  setBalanceError(state, balanceError) {
    state.balanceError = balanceError;
  },
  setGrandTotal(state, grandTotal) {
    state.grandTotal = grandTotal;
  },
  setFetchingDeposit(state, fetchingDeposit) {
    state.fetchingDeposit = fetchingDeposit;
  }
}

const actions = {
  async fetchMatter({commit, dispatch},{payload}) {
    const matterUid = payload.matterUid;
    try {
      const res = await matterService.get(matterUid)
      commit('setMatter', res.data)
      return res.data
    } catch (e) {
      console.error('error', e);
    }
  },
  async getNewInvoice({commit, dispatch},{payload}) {
    const clientUid = payload.clientUid;
    const matterUid = payload.matterUid;
    const copyUid = payload.copyUid;
    const fromEstimateUid = payload.fromEstimateUid;
    try {
      const res = await itemizableService.newInvoice(clientUid, matterUid, copyUid, fromEstimateUid)
      commit('setInvoice', res.data)
      return res.data
    } catch (e) {
      console.error('error', e);
    }

  },
  async getNewEstimate({commit, dispatch, rootGetters},{payload}) {
    const clientUid = payload.clientUid;
    const matterUid = payload.matterUid;
    const copyUid = payload.copyUid;

    try {
      const res = await itemizableService.newEstimate(clientUid, matterUid, copyUid)
      let aiEstimateRecommendation = null;
      if (rootGetters.hasFeature('bizai_cs_recommendations')) {
        try {
          postMessageService.post('povMessage', {
            eventName: 'update-store',
            payload: { type: 'BizaiStore/fetchStaffAiSettings', payload: {} }
          });
          const staffAiSettings = await postMessageService.getDataFromPOVStore('BizaiStore/staffAiSettings');
          const aiRecommendationSettings = staffAiSettings.data?.aiRecommendations;
          if (aiRecommendationSettings?.enabled && aiRecommendationSettings.subOptions.estimate) {
            await dispatch('bizAiModule/fetchRecommendations', {clientUid, matterUid}, {root: true});
            const aiRecommendation = rootGetters['bizAiModule/getRecommendationByMatter'](matterUid);
            aiEstimateRecommendation = aiRecommendation?.actions.find(a => a.action === 'estimate');
          }
        } catch (e) {
          console.log('error fetching ai recommendation', e);
        }
      }
      if (aiEstimateRecommendation) {
        commit('setBlankEstimate', res.data);
        const estimate = {...res.data, ...aiEstimateRecommendation.payload.estimate};
        commit('setEstimate', estimate);
      } else {
        commit('setBlankEstimate', null);
        commit('setEstimate', res.data)
      }
    } catch (e) {
      console.error('error', e);
    }

  },
  clearAiEstimateRecommendation({commit, state, dispatch}, uid) {
    commit('setEstimate', state.blankEstimate);
    dispatch('bizAiModule/updateRecommendation', {uid}, {root: true});
  },
  async fetchServiceList({getters, commit, dispatch}, {payload}) {
    const businessUid = payload.businessUid ;
    //Fetch once or if forced to fetch
    if (payload.force || !getters.serviceList) {
      try {
        const res = await servicesService.fetchServicesCategories({business_uid: businessUid});
        let categories = res.data.categories
        const getServicesParams = {
          business_id: businessUid,
          page: 0,
          per_page: 100,
          include_hidden: true,
          only_visible_services: false,
          has_future_instance: false,
          use_platform_api: true,
          extra_decorator_fields: true,
        };
        let prms = categories
          .filter(category => category.num_of_services !== 0)
          .map(category => (
            servicesService.fetchServicesOfCategory({category_id: category.id, category_params: getServicesParams})
          )
        )
        const services = await Promise.all(prms)
          .then(resServices => resServices.flatMap(res => res.data.services))
        commit('setServiceList', services)
      } catch (e) {
        console.log(e)
      }
    }
  },
  async fetchServiceCategories({commit, dispatch},{payload}) {
    const businessUid = payload.businessUid ;
    try {
      servicesService.fetchServicesCategories({business_uid: businessUid}).then(res => {
        let categories = res.data.categories
        return Promise.all(categories.map(async (category) => {
          category.services = await servicesService.getAllServicesOfCategory(category.id, businessUid);
          commit('addCategory', category);
        }));
      }).finally(() => {
        commit('setServicesLoaded', true);
      });

      productService.getAllProducts(products => {
        products.forEach((product) => {
          product.type = "product";
          product.charge_type = 'paid';
        });
        const productsCategory = {
          id: "products",
          name: "Products",
          services: products,
          num_of_services: products.length,
        };
        commit('addCategory', productsCategory);
      }).finally(() => {
        commit('setProductsLoaded', true);
      });

      packagesService.getAllPackages(packages => {
        packages.forEach((pPackage) => {
          pPackage.type = "package";
          pPackage.charge_type = 'paid';
        })
        const packagesCategory = {
          id: "packages",
          name: "Packages",
          services: packages,
          num_of_services: packages.length,
        }
        commit('addCategory', packagesCategory);
      }).finally(() => {
        commit('setPackagesLoaded', true);
      });
    } catch (e) {
      console.error('error', e);
    }
  },

  async fetchOpenPayments({commit, dispatch},{payload}) {
    const filterBy = {
      per_page: 30,
      client: payload.clientUid,
      conversation_id: payload.matterUid,
      by_status: "outstanding,past_due",
      by_type: "'Meeting','EventAttendance','ClientBookingPackage','ProductOrder'",
      sort_by: 'created_at',
      not_partially_paid: true,
      full: true,
    }
    
    const data = await paymentsStatusesService.listAll(filterBy);
    commit('setOpenPayments', data);
  },
  async fetchMatterPayments({commit, dispatch},{payload}) {
    const filterBy = {
      per_page: 25,
      client_uid: payload.clientUid,
      matter_id: payload.matterUid,
      by_currency: payload.currency,
      by_status: "paid",
      sort_by: 'paid_at:asc',
      exclude_items: true,
      unassigned: true
    }
    const res = await paymentsService.list(filterBy)
    const data = res.data || [];
    commit('setMatterPayments', data);
  },
  setItemizable({commit, dispatch},{payload}) {
    if (payload.type === 'invoice') {
      commit('setInvoice', payload.itemizable)
    } else if (payload.type === 'estimate') {
      commit('setEstimate', payload.itemizable)
    }
  },
  setPaymentAsDeposit({commit}, {payload}){
    commit('setPaymentAsDeposit', payload.paymentAsDeposit)
  },
  setRequestedDeposit({commit}, {payload}){
    commit('setRequestedDeposit', payload.requestedDeposit)
  },
  setBalanceError({commit}, {payload}){
    commit('setBalanceError', payload.balanceError)
  },
  setGrandTotal({commit}, {payload}){
    commit('setGrandTotal', payload.grandTotal)
  },
  setFetchingDeposit({commit}, {payload}){
    commit('setFetchingDeposit', payload.fetchingDeposit)
  },
  clearOpenPayments({commit}) {
    commit('setOpenPayments', []);
  },
  clearMatterPayments({commit}) {
    commit('setMatterPayments', null);
  },
  async fetchContact({commit, state}) {
    const {uid} = state.matter.matter.contacts[0];
    let {error, data} = await contactService.getOrigContact(uid);

    if (error) {
      console.error(error);
      commit('setContact', null)
      return;
    }

    commit('setContact', data.contact)
  },
  async fetchSettings({commit}) {
    try {
      const settings = await settingsService.get()
      commit('setSettings', settings)
    } catch (e) {
      console.log("fetchSettings", e)
    }
  },
  async updateService({commit, dispatch}, {payload}) {
    await servicesService.updateService(payload.service_uid, payload.params);
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};
